import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseHttpService } from '@nimbus-library/features/shared/services/base-http/base-http.service';
import { FeatureUsageOrigin } from '@nimbus-library/utils/feature-usage-origin';
import { IUserSessionLive } from '@nimbus-global/features/reportingLiveData/models/user-session.model';
import { ClientSettings } from '../../models/client-settings';
import { TeamInfo } from '../../models/team-info';
import { TeamInfoLinksToOpen } from '../../models/team-info-links-to-open';
import { ConversationCodesInfo, Modality, TaskParametersResponse, TaskFlowDetails } from '../../models/task-types.model';
import { TaskAttributes, OutboundCallAttributes } from '../../models/task-attributes.model';
import { NotAvailableReasonDetails, submitNotAvailableReason as SubmitNotAvailableReason } from '../../models/not-available-reason';
import { CallTransferRequest, ConsultationCallToServiceRequest } from '../../models/call-transfer';
import { ApiRoutes } from './api-routes.consts';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  public constructor(private readonly httpService: BaseHttpService) {}

  public getSettings(): Observable<ClientSettings> {
    return this.httpService.get(ApiRoutes.Settings.getClientSettings());
  }

  public getServicesMapping(): Observable<TeamInfo[]> {
    return this.httpService.get(ApiRoutes.Services.getServicesMapping());
  }

  public getServiceContext(callerId: string, teamId: string): Observable<TeamInfoLinksToOpen> {
    return this.httpService.post(ApiRoutes.Services.getServiceContext(), { callerId, teamId });
  }

  public setServiceCallAttributes(callAttributes: TaskAttributes): Observable<void> {
    return this.httpService.post(ApiRoutes.Services.setServiceCallAttributes(), callAttributes);
  }

  public getUserSessionsById(userId: string): Observable<IUserSessionLive[]> {
    return this.httpService.get(ApiRoutes.Services.getUserSessionsById(userId));
  }

  public getCodes(teamId: string, conversationId: string, userSessionId: string): Observable<ConversationCodesInfo> {
    return this.httpService.get(ApiRoutes.Services.getCodesAndTags(teamId, conversationId, userSessionId));
  }

  public getContextParameters(teamId: string, conversationId: string, userSessionId: string): Observable<TaskParametersResponse> {
    return this.httpService.get(ApiRoutes.Services.getContextParameters(teamId, conversationId, userSessionId));
  }

  public getCallFlow(teamId: string, conversationId: string, userSessionId: string): Observable<TaskFlowDetails[]> {
    return this.httpService.get(ApiRoutes.Services.getCallFlow(teamId, conversationId, userSessionId));
  }

  public getChatDeepLink(sessionId: string): Observable<string> {
    return this.httpService.get(ApiRoutes.AcsDriver.getChatInfo(sessionId)).pipe(map((result) => result?.agentChatDeepLink || ''));
  }

  public startOutboundCall(outboundCallAttributes: OutboundCallAttributes): Observable<void> {
    const headers = { 'X-Feature-Usage-Origin': FeatureUsageOrigin.AssistantOutbound };
    return this.httpService.post(ApiRoutes.Calls.startOutboundCall(), outboundCallAttributes, headers);
  }

  public blindTransfer(
    unifiedConversationId: string,
    conversationId: string,
    modality: Modality,
    callTransferRequest: CallTransferRequest,
  ): Observable<void> {
    return this.httpService.post(ApiRoutes.Calls.blindTransfer(unifiedConversationId, conversationId, modality), callTransferRequest);
  }

  public blindTransferToService(
    unifiedConversationId: string,
    conversationId: string,
    modality: Modality,
    target: string,
  ): Observable<void> {
    return this.httpService.post(ApiRoutes.Calls.blindTransferToService(unifiedConversationId, conversationId, modality, target));
  }

  public consultativeTransfer(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    callTransferRequest: CallTransferRequest,
  ): Observable<void> {
    return this.httpService.post(
      ApiRoutes.Calls.consultativeTransfer(unifiedConversationId, conversationId, user365Id),
      callTransferRequest,
    );
  }

  public mergeConsultationCall(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    consultationCallId: string,
  ): Observable<void> {
    return this.httpService.put(
      ApiRoutes.Calls.mergeConsultationCall(unifiedConversationId, conversationId, user365Id, consultationCallId),
    );
  }

  public swapConsultationCall(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    consultationCallId: string,
  ): Observable<void> {
    return this.httpService.put(ApiRoutes.Calls.swapConsultationCall(unifiedConversationId, conversationId, user365Id, consultationCallId));
  }

  public transferConsultationCall(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    consultationCallId: string,
  ): Observable<void> {
    return this.httpService.put(
      ApiRoutes.Calls.transferConsultationCall(unifiedConversationId, conversationId, user365Id, consultationCallId),
    );
  }

  public terminateConsultationCall(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    consultationCallId: string,
  ): Observable<void> {
    return this.httpService.put(
      ApiRoutes.Calls.terminateConsultationCall(unifiedConversationId, conversationId, user365Id, consultationCallId),
    );
  }

  public parkSession(callId: string, user365Id: string): Observable<void> {
    return this.httpService.put(ApiRoutes.Calls.park(callId, user365Id));
  }

  public hold(unifiedConversationId: string, user365Id: string): Observable<void> {
    return this.httpService.put(ApiRoutes.Calls.hold(unifiedConversationId, user365Id));
  }

  public resume(unifiedConversationId: string, user365Id: string): Observable<void> {
    return this.httpService.put(ApiRoutes.Calls.resume(unifiedConversationId, user365Id));
  }

  public terminateConsultationCallAndResume(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    consultationCallId: string,
  ): Observable<void> {
    return this.httpService.put(
      ApiRoutes.Calls.terminateConsultationCallAndResume(unifiedConversationId, conversationId, user365Id, consultationCallId),
    );
  }

  public consultationCallToService(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    request: ConsultationCallToServiceRequest,
  ): Observable<void> {
    return this.httpService.post(ApiRoutes.Calls.consultationCallToService(unifiedConversationId, conversationId, user365Id), request);
  }

  public mergeConsultationCallToService(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    consultationCallId: string,
  ): Observable<void> {
    return this.httpService.put(
      ApiRoutes.Calls.mergeConsultationCallToService(unifiedConversationId, conversationId, user365Id, consultationCallId),
    );
  }

  public swapConsultationCallToService(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    consultationCallId: string,
  ): Observable<void> {
    return this.httpService.put(
      ApiRoutes.Calls.swapConsultationCallToService(unifiedConversationId, conversationId, user365Id, consultationCallId),
    );
  }

  public transferConsultationCallToService(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    consultationCallId: string,
  ): Observable<void> {
    return this.httpService.put(
      ApiRoutes.Calls.transferConsultationCallToService(unifiedConversationId, conversationId, user365Id, consultationCallId),
    );
  }

  public terminateConsultationCallToService(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    consultationCallId: string,
  ): Observable<void> {
    return this.httpService.put(
      ApiRoutes.Calls.terminateConsultationCallToService(unifiedConversationId, conversationId, user365Id, consultationCallId),
    );
  }

  public terminateConsultationCallToServiceAndResume(
    unifiedConversationId: string,
    conversationId: string,
    user365Id: string,
    consultationCallId: string,
  ): Observable<void> {
    return this.httpService.put(
      ApiRoutes.Calls.terminateConsultationCallToServiceAndResume(unifiedConversationId, conversationId, user365Id, consultationCallId),
    );
  }

  public safeTransfer(
    unifiedConversationId: string,
    conversationId: string,
    modality: Modality,
    callTransferRequest: CallTransferRequest,
  ): Observable<void> {
    return this.httpService.post(ApiRoutes.Calls.safeTransfer(unifiedConversationId, conversationId, modality), callTransferRequest);
  }

  public safeTransferToService(
    unifiedConversationId: string,
    conversationId: string,
    modality: Modality,
    target: string,
  ): Observable<void> {
    return this.httpService.post(ApiRoutes.Calls.safeTransferToService(unifiedConversationId, conversationId, modality, target));
  }

  public isAllowTaskActivationWhileInActiveTaskEnabled(): Observable<boolean> {
    return this.httpService.get(ApiRoutes.FeatureManagement.isAllowTaskActivationWhileInActiveTaskEnabled());
  }

  public getNotAvailableReasonRequests(): Observable<NotAvailableReasonDetails> {
    return this.httpService.get(ApiRoutes.Nar.getNotAvailableReasonRequests());
  }

  public submitNotAvailableReason(nar: SubmitNotAvailableReason): Observable<void> {
    return this.httpService.post(ApiRoutes.Nar.submitNotAvailableReason(), nar);
  }

  public acceptSession(callId: string, sessionId: string, modality: Modality): Observable<void> {
    return this.httpService.post(ApiRoutes.Session.accept(callId, sessionId, modality));
  }
  public declineSession(callId: string, sessionId: string, modality: Modality): Observable<void> {
    return this.httpService.post(ApiRoutes.Session.decline(callId, sessionId, modality));
  }
  public terminateSession(callId: string, sessionId: string, modality: Modality): Observable<void> {
    return this.httpService.post(ApiRoutes.Session.terminate(callId, sessionId, modality));
  }
}
